import { storedModelOdd } from "../helpers/leagueStoredModels";
import { liveFixture } from "../helpers/dayFixtureWorkflow";
import { formatFixtureResult } from "../helpers/fixtureResult";
import { fixtureProcessingStatus } from "../helpers/fixtureProcessingStatus";
import i18next from "i18next";
import AwaitingCompilationIcon from "@mui/icons-material/HourglassEmpty";
import CompiledIcon from "@mui/icons-material/Addchart";
import CompilationConfirmedIcon from "@mui/icons-material/InsertChart";
import AwaitingEvaluationIcon from "@mui/icons-material/GppMaybe";
import EvaluationConfirmedIcon from "@mui/icons-material/CheckCircle";
import HelpIcon from "@mui/icons-material/Help";
import { OddsTooltip } from "../components/OddsTooltip";
import { getLeagueDisplayName, LeagueDisplay } from "../components/LeagueDisplay";
import { STATI } from "../helpers/stati";
import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
} from "@mui/x-data-grid-pro";
import { IconButton, Tooltip, Box } from "@mui/material";
import ExpandIcon from "@mui/icons-material/Expand";
import AsianHandicapMenu from "./AsianHandicapMenu";
import { fetchFromApi } from "../helpers/fetchFromApi";

function CustomDetailPanelToggle() {
  return (
    <IconButton aria-label="expand" title={i18next.t("help.tooltips.expandFixtureDetails")}>
      <ExpandIcon />
    </IconButton>
  );
}

async function getCountryRequest(country_id) {
  const response = await (
    await fetchFromApi(`fetch-country/${country_id}`)
  ).json();
  return response.data
}

export const fixtureColumns = [
  {
    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    headerName: "Expand",
    type: "string",
    field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
    renderCell: (params) => <CustomDetailPanelToggle {...params} />,
  },
  {
    headerName: "status",
    renderHeader: () => <span>{i18next.t("help.dayWorkflow.columns.status")}</span>,
    field: "status",
    width: 48,
    minWidth: 48,
    renderCell: ({ row }) => {
      const status = fixtureProcessingStatus(row);
      const color = ({live:'#ff7777',complete:'#83dfa7',compiled:'#83dfa7',error:'#ff9999',model_missing:'#ffcd75',model_read_error:'#ffcd75',no_result:'#b9c6d4'})[status.code] || '#e3c798';
      const Icon = ['complete','compiled'].includes(status.code) ? EvaluationConfirmedIcon : status.code==='open' ? AwaitingEvaluationIcon : status.code==='pending' ? AwaitingCompilationIcon : HelpIcon;
      return <Tooltip title={status.label} arrow><Box component="span" tabIndex={0} aria-label={status.label} data-status={status.code} sx={{color,display:'flex',alignItems:'center'}}><Icon fontSize="small"/></Box></Tooltip>;
    },
  },
  {
    headerName: "kickoff time",
    renderHeader: () => <span>{i18next.t("help.dayWorkflow.columns.time")}</span>,
    field: "details",
    width: 70,
    renderCell: ({value,row}) => {
      return (
      <div title={value.date.toLocaleDateString()}>
        <div>
          {row.show_fixture_date && value.date.toLocaleDateString("en-GB", {
            weekday: "short",
            day: "numeric",
            month: "numeric",
          })}{" "}
          {value.date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>
    )},
  },
  {
    headerName: "competition",
    renderHeader: () => <span>{i18next.t("help.dayWorkflow.columns.league")}</span>,
    field: "league",
    flex: 1,
    // Grid text paths (including export) must never stringify the league object.
    valueFormatter: ({ value }) => getLeagueDisplayName(value),
    renderCell: ({ value }) => {
      if (!getLeagueDisplayName(value)) {
        return null;
      }

      return <LeagueDisplay league={value} compact />;
    },
  },
  {
    headerName: "home team",
    renderHeader: () => <span>{i18next.t("help.dayWorkflow.columns.home")}</span>,
    field: "localTeam",
    flex: 1.5,
    renderCell: ({ value }) => {
      return (<div
        onMouseEnter={(event) => {
          getCountryRequest(value.country_id).then((response) => {
            event.target.title = response.official_name;
          })
        }}
        style={{ display: "flex", alignItems: "center", gap: "0.5em", fontWeight:700, fontSize:13 }}>
        {<img src={value.image_path} style={{ height: "1.5em" }} alt="" />}
        {value.name}
      </div>
      )
    },
  },
  {
    headerName: "away team",
    renderHeader: () => <span>{i18next.t("help.dayWorkflow.columns.away")}</span>,
    field: "visitorTeam",
    flex: 1.5,
    renderCell: ({ value }) => (
      <div
        onMouseEnter={(event) => {
          getCountryRequest(value.country_id).then((response) => {
            event.target.title = response.official_name;
          })
        }}
        style={{ display: "flex", alignItems: "center", gap: "0.5em", fontWeight:700, fontSize:13 }}>
        {<img src={value.image_path} style={{ height: "1.5em" }} alt="" />}
        {value.name}
      </div>
    ),
  },
  {
    headerName: "result",
    renderHeader: () => <span>{i18next.t("help.dayWorkflow.columns.score")}</span>,
    description: "Fixture result after game is finished. Could also be live result.",
    field: "scores",
    flex: 1,
    renderCell: ({ value, row }) => {
      if (liveFixture(row)) {
        const current = formatFixtureResult(row, "-");
        if (current) return <span>{current}</span>;
      }
      if (!value || Object.keys(value).length === 0) {
        return (<span>-</span>);
      }
      if (value[0].finish === "POSTPONED" || value[0].finish === "CANCELLED") {
        return <span>-</span>;
      }
      var afterSecondTimeHome = getByTypeId(value, 2, "home");
      var afterSecondTimeAway = getByTypeId(value, 2, "away");

      var result = "-"
      if (afterSecondTimeHome !== "" && afterSecondTimeAway !== "") {
        result = afterSecondTimeHome + "-" + afterSecondTimeAway;
      }

      var afterFirstTimeHome = getByTypeId(value, 1, "home");
      var afterFirstTimeAway = getByTypeId(value, 1, "away");

      if (afterFirstTimeHome !== "" && afterFirstTimeAway !== "") {
        result += " (" + afterFirstTimeHome + "-" + afterFirstTimeAway + ")";
      }

      var afterExtraTimeHome = getByTypeId(value, 3, "home");
      var afterExtraTimeAway = getByTypeId(value, 3, "away");
      if (afterExtraTimeHome !== "" && afterExtraTimeAway !== "") {
        result += " (" + afterExtraTimeHome + "-" + afterExtraTimeAway + ")";
      }

      var afterPenaltyShootoutHome = getByTypeId(value, 5, "home");
      var afterPenaltyShootoutAway = getByTypeId(value, 5, "away");
      if (afterPenaltyShootoutHome !== "" && afterPenaltyShootoutAway !== "") {
        result += " (" + afterPenaltyShootoutHome + "-" + afterPenaltyShootoutAway + ")";
      }

      return <span>{result}</span>;
    },
  },
  ...['result','statistic'].map(model => ({
    field: model + '_1x2', headerClassName: 'model-odds-header', flex: 1.4, minWidth: 142,
    renderHeader: () => <Box sx={{fontWeight:800,lineHeight:1.35,width:"100%",textAlign:"center"}}>{i18next.t('help.dayWorkflow.models.'+model)}<Box sx={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:1,fontSize:11,textAlign:'center'}}><span>1</span><span>X</span><span>2</span></Box></Box>,
    renderCell: ({row}) => <Box sx={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:0.7,width:'100%',fontWeight:700,fontVariantNumeric:'tabular-nums',textAlign:'center'}}>{['1','X','2'].map(side => {
      const odd=storedModelOdd(model==='result'?row:row.statistic_prediction,'1x2',side);
      return <OddsTooltip key={side} oddsId={row.id} label={side}><span>{odd == null ? '–' : odd.toFixed(2)}</span></OddsTooltip>;
    })}</Box>,
  })),
  {
    headerName: "true home",
    field: "homewin",
    description: "true price for home win. Reciprocal of win probability home side",
    flex: 1,
    renderCell: ({ value, row }) => (
      <OddsTooltip
        oddsId={row.id}
        label="1"
      >
        <span>{value ? (1 / value).toFixed(2) : "-"}</span>
      </OddsTooltip>
    ),
  },
  {
    headerName: "true draw",
    field: "draw",
    description: "true price for the draw. Reciprocal of draw probability",
    flex: 1,
    renderCell: ({ value, row }) => (
      <OddsTooltip
        oddsId={row.id}
        label="X"
      >
        <span>{value ? (1 / value).toFixed(2) : "-"}</span>
      </OddsTooltip>
    ),
  },
  {
    headerName: "true away",
    field: "awaywin",
    description: "true price for away win. Reciprocal of away win probability",
    flex: 1,
    renderCell: ({ value, row }) => (
      <OddsTooltip
        oddsId={row.id}
        label="2"
      >
        <span>{value ? (1 / value).toFixed(2) : "-"}</span>
      </OddsTooltip>
    ),
  },
  {
    headerName: "Extras",
    field: "agl",
    description: "See more for that fixture",
    flex: 1,
    renderCell: ({ row }) => <AsianHandicapMenu row={row} />,
  }
];
function getByTypeId(value, typeId, side) {
  const t = value.find((it) => it.type_id === typeId && it.score.participant === side);
  return t ? t.score.goals : "";
}
